import { useState } from 'react';
import { Button, Upload, message, Typography, Table, Alert, Card, Space, Tag } from 'antd';
import {
  InboxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { bulkImportStudents, type BulkImportResult } from '@/services/onboarding.service';
import { maskEmail } from '@/utils/format';
import type { RcFile } from 'antd/es/upload';

const { Title, Text } = Typography;
const { Dragger } = Upload;

export default function BulkImportStudentsPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  const importMutation = useMutation({
    mutationFn: (file: File) => bulkImportStudents(file),
    onSuccess: (res) => {
      setResult(res.data);
      const { created, skipped, errors } = res.data;
      if (errors.length === 0) {
        message.success(`Import complete: ${created} created, ${skipped} skipped`);
      } else {
        message.warning(`Import done with errors: ${created} created, ${skipped} skipped, ${errors.length} failed`);
      }
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Import failed');
    },
  });

  const handleUpload = (file: RcFile) => {
    if (!file.name.endsWith('.csv')) {
      message.error('Only CSV files are accepted');
      return false;
    }
    setSelectedFile(file);
    setResult(null);
    return false; // Prevent auto-upload
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      message.warning('Please select a CSV file first');
      return;
    }
    importMutation.mutate(selectedFile);
  };

  const errorColumns = [
    {
      title: 'Row',
      dataIndex: 'row',
      key: 'row',
      width: 70,
      render: (v: number) => <Tag style={{ borderRadius: 12 }}>#{v}</Tag>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{maskEmail(v)}</span>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (v: string) => <Text type="danger" style={{ fontSize: 12 }}>{v}</Text>,
    },
  ];

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>Bulk Import Students</Title>
        <Text style={{ color: '#64748b', fontSize: 13 }}>
          Upload a CSV file to create multiple student accounts at once
        </Text>
      </div>

      <Card style={{ borderRadius: 12, marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Text style={{ fontWeight: 600, color: '#0f172a', display: 'block', marginBottom: 4 }}>
            CSV Format Requirements
          </Text>
          <Text style={{ color: '#64748b', fontSize: 13 }}>
            Required columns: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>name</code>,{' '}
            <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>email</code>,{' '}
            <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>parent_email</code>,{' '}
            <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4 }}>grade</code>
          </Text>
        </div>

        <Dragger
          accept=".csv"
          maxCount={1}
          beforeUpload={handleUpload}
          showUploadList={selectedFile ? {
            showRemoveIcon: true,
          } : false}
          fileList={selectedFile ? [{ uid: '-1', name: selectedFile.name, status: 'done' }] : []}
          onRemove={() => { setSelectedFile(null); setResult(null); }}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: '#6366f1', fontSize: 40 }} />
          </p>
          <p style={{ fontSize: 14, color: '#0f172a', fontWeight: 500 }}>
            Click or drag a CSV file here
          </p>
          <p style={{ fontSize: 12, color: '#94a3b8' }}>
            Only .csv files accepted · No size limit for imports
          </p>
        </Dragger>

        <Button
          type="primary"
          icon={<UploadOutlined />}
          onClick={handleSubmit}
          loading={importMutation.isPending}
          disabled={!selectedFile}
          size="large"
          style={{ width: '100%', background: '#6366f1', borderColor: '#6366f1' }}
        >
          {importMutation.isPending ? 'Importing…' : 'Import Students'}
        </Button>
      </Card>

      {/* Results */}
      {result !== null && (
        <Card style={{ borderRadius: 12 }}>
          <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: '#0f172a' }}>
            Import Results
          </Title>

          <Space size={12} style={{ marginBottom: 16, flexWrap: 'wrap' as const }}>
            <div style={{
              background: '#d1fae5',
              borderRadius: 10,
              padding: '12px 20px',
              textAlign: 'center',
              minWidth: 100,
            }}>
              <CheckCircleOutlined style={{ color: '#10b981', fontSize: 20, display: 'block', marginBottom: 4 }} />
              <div style={{ fontSize: 24, fontWeight: 700, color: '#065f46' }}>{result.created}</div>
              <div style={{ fontSize: 12, color: '#047857', fontWeight: 500 }}>Created</div>
            </div>

            <div style={{
              background: '#fef3c7',
              borderRadius: 10,
              padding: '12px 20px',
              textAlign: 'center',
              minWidth: 100,
            }}>
              <WarningOutlined style={{ color: '#f59e0b', fontSize: 20, display: 'block', marginBottom: 4 }} />
              <div style={{ fontSize: 24, fontWeight: 700, color: '#92400e' }}>{result.skipped}</div>
              <div style={{ fontSize: 12, color: '#b45309', fontWeight: 500 }}>Skipped</div>
            </div>

            <div style={{
              background: result.errors.length > 0 ? '#fee2e2' : '#f1f5f9',
              borderRadius: 10,
              padding: '12px 20px',
              textAlign: 'center',
              minWidth: 100,
            }}>
              <CloseCircleOutlined style={{ color: result.errors.length > 0 ? '#ef4444' : '#94a3b8', fontSize: 20, display: 'block', marginBottom: 4 }} />
              <div style={{ fontSize: 24, fontWeight: 700, color: result.errors.length > 0 ? '#991b1b' : '#94a3b8' }}>{result.errors.length}</div>
              <div style={{ fontSize: 12, color: result.errors.length > 0 ? '#b91c1c' : '#94a3b8', fontWeight: 500 }}>Errors</div>
            </div>
          </Space>

          {result.errors.length === 0 && (
            <Alert
              type="success"
              message={`All done! ${result.created} student${result.created !== 1 ? 's' : ''} created successfully.`}
              style={{ borderRadius: 8 }}
            />
          )}

          {result.errors.length > 0 && (
            <div>
              <Alert
                type="warning"
                message={`${result.errors.length} row${result.errors.length !== 1 ? 's' : ''} failed to import`}
                description="Review the errors below and fix your CSV before re-importing the failed rows."
                style={{ borderRadius: 8, marginBottom: 12 }}
              />
              <Button
                type="link"
                style={{ padding: 0, marginBottom: 8 }}
                onClick={() => setErrorsExpanded((v) => !v)}
              >
                {errorsExpanded ? 'Hide error details' : `Show ${result.errors.length} error detail${result.errors.length !== 1 ? 's' : ''}`}
              </Button>
              {errorsExpanded && (
                <Table
                  columns={errorColumns}
                  dataSource={result.errors}
                  rowKey={(r) => `${r.row}-${r.email}`}
                  pagination={false}
                  size="small"
                  style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}
                />
              )}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
